import { useEffect, useId, useMemo, useState, type FormEvent } from "react";
import { Link, useLocation, useParams } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import { isNotFound, isUnauthorized } from "../../api/errors";
import {
  useDeleteService,
  useDeployments,
  useDeployService,
  useNodes,
  usePlacements,
  useProject,
  useService,
  useUpdateService,
} from "../../api/queries";
import type { Deployment } from "../../api/types";
import { ErrorState } from "../../components/AsyncStates";
import { Banner } from "../../components/Banner";
import { Breadcrumb } from "../../components/Breadcrumb";
import { Button } from "../../components/Button";
import { EnvironmentBadge } from "../../components/EnvironmentBadge";
import { DetailPageSkeleton } from "../../components/Skeleton";
import { ServiceStatusPill, StatePill } from "../../components/StatePill";
import { useToast } from "../../toast/ToastContext";
import { useEnvironmentsById } from "../environments/useEnvironmentsById";
import { PromoteServiceModal } from "../environments/PromoteServiceModal";
import { CapturedConfigurationSection } from "./CapturedConfigurationSection";
import { ConnectionsSection } from "./ConnectionsSection";
import { DeployInFlightCard } from "./DeployInFlightCard";
import { DeploymentTimeline } from "./DeploymentTimeline";
import { DomainsSection } from "./DomainsSection";
import { PlacementsSection } from "./PlacementsSection";
import { RollbackDialog } from "./RollbackDialog";
import { ServiceContainersSection } from "./ServiceContainersSection";
import { ServiceLiveLogsSection } from "./ServiceLiveLogsSection";
import { UnadoptServiceDialog } from "./UnadoptServiceDialog";
import "../../components/Form.css";
import "./services.css";

const ACTIVE_DEPLOYMENT_STATUSES = new Set(["pending", "deploying"]);

export function ServiceDetailPage() {
  const { projectId, serviceId } = useParams<{ projectId: string; serviceId: string }>();
  const { session } = useAuth();
  const { pushToast } = useToast();
  const [, navigate] = useLocation();

  const { data: service, isPending, isError, error, refetch } = useService(projectId ?? "", serviceId);
  // Read-only, for the breadcrumb's project crumb only — shares the same query cache
  // ProjectDetailPage populates, so this costs no extra request once that page has been
  // visited. A miss (deep link straight to a service) degrades to the raw id, never
  // blocking this page's own load (mirrors EnvironmentDetailPage's `sourceName`).
  const { data: project } = useProject(projectId);
  // Placements carry the durable, server-derived adopted signal (issue #330). Shares the
  // same query cache as PlacementsSection below, so this costs no extra request.
  const { data: placementsPage } = usePlacements(projectId ?? "", serviceId ?? "");
  // The deploy timeline at the page level too (shares `DeploymentTimeline`'s query cache,
  // no extra request) — drives the header's env pill, the deploy-in-flight card, and the
  // header "Rollback…" button's default target (ServiceRollout board).
  const { data: deploymentsPage } = useDeployments(projectId ?? "", serviceId);
  const { data: nodesPage } = useNodes({ limit: 100 });
  const environmentsById = useEnvironmentsById();
  const updateMutation = useUpdateService(projectId ?? "");
  const deployMutation = useDeployService(projectId ?? "");
  const deleteMutation = useDeleteService(projectId ?? "");

  const imageId = useId();
  const desiredCountId = useId();
  const [image, setImage] = useState("");
  const [desiredCount, setDesiredCount] = useState("");
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [unadoptOpen, setUnadoptOpen] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<Deployment | null>(null);

  // Seeds the form from the loaded service and re-seeds whenever the
  // service *id* changes — an operator's in-progress edit is never silently
  // clobbered by a background refetch.
  useEffect(() => {
    if (!service) return;
    setImage(service.image ?? "");
    setDesiredCount(String(service.desiredCount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id]);

  // Mirrors the server's authz matrix (service:update is granted to
  // operator/admin only) — this only hides the control (S-8).
  const canManage = canManageFleet(session?.role);
  // The durable, server-derived adopted signal (issue #330): a placement still bound to
  // its original container (containerId != "", so `adopted` is true) means "adopted, not
  // yet taken over" — the container keeps running, so un-adopt merely detaches it. Read
  // from truth (the placement), never inferred from the shape of the captured spec.
  const adopted = placementsPage?.items.some((p) => p.adopted) ?? false;

  // The header's environment badge (ServiceRollout board) — resolved from the first
  // placement's node, the same "one representative value" simplification
  // `useMyServiceRows` makes for the Operate landing table. Absent when unplaced or the
  // node carries no environment binding; never a guessed default (A-10).
  const nodesById = useMemo(() => new Map(nodesPage?.items.map((n) => [n.id, n])), [nodesPage]);
  const firstPlacementNode = placementsPage?.items[0] ? nodesById.get(placementsPage.items[0].nodeId) : undefined;

  // The active (non-terminal) deployment, when one exists — drives the deploy-in-flight
  // card. `useDeployments` is already newest-first, so index 0 is the candidate.
  const deployments = deploymentsPage?.items ?? [];
  const activeDeployment = deployments[0] && ACTIVE_DEPLOYMENT_STATUSES.has(deployments[0].status) ? deployments[0] : undefined;
  // The header "Rollback…" button's default target — the newest deployment that actually
  // reached `"running"` (the only status the server accepts a rollback target in,
  // ADR-0031 §2b), which is exactly the entry an in-flight rollout would be reverting to.
  const rollbackDefaultTarget = deployments.find((d) => d.status === "running");

  function handleUnadoptConfirm() {
    if (!serviceId || !projectId) return;
    deleteMutation.mutate(
      { id: serviceId },
      {
        onSuccess: () => {
          pushToast({ title: "Service un-adopted — the container keeps running.", tone: "success" });
          setUnadoptOpen(false);
          navigate(`/projects/${projectId}`);
        },
        onError: (err) => {
          setUnadoptOpen(false);
          pushToast({
            title: "Couldn't un-adopt this service",
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  function handleUpdateSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!serviceId) return;
    const parsedCount = Number.parseInt(desiredCount, 10);
    updateMutation.mutate(
      {
        id: serviceId,
        input: {
          image: image.trim(),
          desiredCount: parsedCount,
        },
      },
      {
        onSuccess: () => pushToast({ title: "Configuration saved.", tone: "success" }),
        onError: (err) =>
          pushToast({
            title: "Couldn't save configuration",
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          }),
      },
    );
  }

  function handleDeploy() {
    if (!serviceId) return;
    deployMutation.mutate(
      { id: serviceId },
      {
        onSuccess: () => pushToast({ title: "Service deployed.", tone: "success" }),
        onError: (err) => {
          // 409 is not the operator's mistake — it means deploy can't run
          // right now for a reason the server explains (mid ADR-0025, that's
          // most often "deploy is being rebuilt on the placement graph," but
          // the same graceful handling covers any other conflict the server
          // reports). Surface it as an informational toast, not an alarming
          // failure — the persistent banner below carries the same message
          // for anyone who missed the toast.
          if (err.status === 409) {
            pushToast({ title: "Deploy not available yet", description: err.message, tone: "info" });
            return;
          }
          pushToast({
            title: "Deploy failed",
            description: err.message || "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  if (isPending) {
    return <DetailPageSkeleton />;
  }

  if (isError) {
    if (isUnauthorized(error)) {
      return <DetailPageSkeleton />;
    }
    if (isNotFound(error)) {
      return (
        <ErrorState
          title="Service not found"
          description="It may have been removed, or the link is out of date. Head back to the project."
          action={
            <Link to={`/projects/${projectId}`} className="button button--secondary">
              Back to the project
            </Link>
          }
        />
      );
    }
    return (
      <ErrorState
        description={error instanceof Error ? error.message : "Couldn't load this service."}
        onRetry={refetch}
      />
    );
  }

  if (!service) return null;

  const parsedCount = Number.parseInt(desiredCount, 10);
  const canSave =
    canManage && image.trim().length > 0 && Number.isInteger(parsedCount) && parsedCount >= 0 && !updateMutation.isPending;

  return (
    <div className="service-detail">
      <Breadcrumb
        items={[
          { label: "Develop", to: "/projects" },
          { label: project?.name ?? projectId ?? "Project", to: `/projects/${projectId}` },
          { label: service.name },
        ]}
      />

      <div className="service-detail__header">
        <div className="service-detail__title-row">
          <h1 className="service-detail__title">{service.name}</h1>
          <ServiceStatusPill status={service.status} />
          {/* Durable, server-derived: shown whenever a placement is still bound to its
              original container (issue #330), not a this-visit-only sessionStorage flag. */}
          {adopted && <StatePill label="Adopted" tone="positive" shape="dot" />}
          {firstPlacementNode?.environmentName && (
            <EnvironmentBadge
              name={firstPlacementNode.environmentName}
              envClass={environmentsById.get(firstPlacementNode.environmentId)?.class}
            />
          )}
        </div>
        {canManage && (
          <div className="service-detail__actions">
            {adopted && (
              <Button variant="danger" onClick={() => setUnadoptOpen(true)}>
                Un-adopt
              </Button>
            )}
            {/* Promote is admin+operator (environment:promote), the same set as
                canManage here — it deploys the source environment's pinned
                bytes onto a downstream environment (ADR-0035 §2/§3). */}
            <Button variant="secondary" onClick={() => setPromoteOpen(true)}>
              Promote
            </Button>
            {/* ServiceRollout board's "Rollback…" — opens the ONE rollback dialog
                (`RollbackDialog`, shared with the timeline's per-row action) defaulting to
                the newest deployment that actually reached "running". Hidden (not
                disabled) when there is no such target yet — a service that has never had a
                successful deploy has nothing to roll back to (A-10). */}
            {rollbackDefaultTarget && (
              <Button variant="secondary" onClick={() => setRollbackTarget(rollbackDefaultTarget)}>
                Rollback…
              </Button>
            )}
            <Button variant="primary" disabled={deployMutation.isPending} onClick={handleDeploy}>
              {deployMutation.isPending ? "Deploying…" : "Deploy"}
            </Button>
          </div>
        )}
      </div>

      {/* Restart is deliberately NOT surfaced here (IA doc §8 names it, but the fleet's
          authz-proxied Docker surface only exposes restart PER CONTAINER, not one atomic
          service-level action) — composing N container-restart calls client-side into a
          single "Restart" button would be a new, unaudited-as-one-action affordance this
          slice doesn't add. Restart per-container from the Containers section below. */}

      {activeDeployment && (
        <DeployInFlightCard projectId={projectId ?? ""} serviceId={serviceId ?? ""} active={activeDeployment} />
      )}

      {adopted && (
        <Banner tone="info" title="Container adopted" className="service-detail__deploy-banner">
          Review the captured configuration below — command, entrypoint, and healthcheck are masked by default —
          before redeploying this service.
        </Banner>
      )}

      {deployMutation.isError && deployMutation.error && (
        <>
          {deployMutation.error.status === 409 ? (
            <Banner tone="warning" title="Deploy not available yet" className="service-detail__deploy-banner">
              {deployMutation.error.message}
            </Banner>
          ) : (
            <Banner tone="danger" title="Deploy failed" className="service-detail__deploy-banner">
              {deployMutation.error.message || "Couldn't deploy this service. Try again in a moment."}
            </Banner>
          )}
        </>
      )}

      <PlacementsSection
        projectId={projectId ?? ""}
        serviceId={serviceId ?? ""}
        serviceName={service.name}
        canManage={canManage}
      />

      <ServiceContainersSection serviceId={serviceId ?? ""} />

      {/* Gated on the real adopted signal (issue #330), not a heuristic over the spec:
          once a service is taken over it is a plain managed service with no captured
          review. */}
      {adopted && <CapturedConfigurationSection service={service} />}

      <ConnectionsSection
        projectId={projectId ?? ""}
        serviceId={serviceId ?? ""}
        serviceName={service.name}
        canManage={canManage}
      />

      <DomainsSection
        projectId={projectId ?? ""}
        serviceId={serviceId ?? ""}
        serviceName={service.name}
        canManage={canManage}
      />

      <section className="service-detail__section" aria-labelledby="service-configure-heading">
        <h2 id="service-configure-heading">Configure</h2>
        <form onSubmit={handleUpdateSubmit} noValidate>
          <label className="form-field" htmlFor={imageId}>
            <span>Image</span>
            <input
              id={imageId}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={image}
              onChange={(e) => setImage(e.target.value)}
              disabled={!canManage}
              required
            />
            <span className="form-field__hint">
              Saving here updates the configuration only — use Deploy above to run it.
            </span>
          </label>

          <label className="form-field" htmlFor={desiredCountId}>
            <span>Desired count</span>
            <input
              id={desiredCountId}
              type="number"
              min={0}
              value={desiredCount}
              onChange={(e) => setDesiredCount(e.target.value)}
              disabled={!canManage}
              required
            />
          </label>

          {canManage && (
            <div className="form-actions">
              <Button type="submit" variant="secondary" disabled={!canSave}>
                {updateMutation.isPending ? "Saving…" : "Save configuration"}
              </Button>
            </div>
          )}
        </form>
      </section>

      <DeploymentTimeline
        projectId={projectId ?? ""}
        serviceId={serviceId ?? ""}
        serviceName={service.name}
        canManage={canManage}
      />

      <ServiceLiveLogsSection serviceId={serviceId ?? ""} />

      {canManage && (
        <PromoteServiceModal
          open={promoteOpen}
          projectId={projectId ?? ""}
          serviceId={serviceId ?? ""}
          serviceName={service.name}
          onClose={() => setPromoteOpen(false)}
        />
      )}

      {canManage && (
        <RollbackDialog
          projectId={projectId ?? ""}
          serviceId={serviceId ?? ""}
          serviceName={service.name}
          target={rollbackTarget}
          fromDeployment={deployments[0]}
          onClose={() => setRollbackTarget(null)}
        />
      )}

      {canManage && (
        <UnadoptServiceDialog
          serviceName={unadoptOpen ? service.name : null}
          isRemoving={deleteMutation.isPending}
          onCancel={() => setUnadoptOpen(false)}
          onConfirm={handleUnadoptConfirm}
        />
      )}
    </div>
  );
}
